import React, { useEffect, useState } from "react";
import { api } from "./api";

/**
 * The review queue (build plan §7.1 #3): every person on the roster, filtered
 * to what still needs a reviewer by default. Desktop-first; below tablet width
 * the table becomes a list.
 */

export interface QueuePerson {
  personKey: string;
  lastName: string;
  firstName: string;
  city: string;
  reviewStatus: string;
  reviewedAtUtc: string;
  flagTypes: string[];
  expectedReasons: string[];
  familyUpdatedAfterReview: boolean;
  /** Attendance hold (plan §7.1 #7): the Director's decision, and whether one is asked for. */
  attendanceHold: string;
  holdRequested: boolean;
  /** M3: the frozen medication seed vs the live application; null when not frozen. */
  changedSinceFreeze?: boolean | null;
}

export const FLAG_LABEL: Record<string, string> = {
  MissingDocument: "Missing document",
  ClarificationNeeded: "Clarification needed",
  ClinicalConcern: "Clinical concern",
  NeedsSupervisorReview: "Needs supervisor review",
  ExpectedAtMedStation: "Expected at med station",
  AttendanceHoldRequested: "Attendance hold: decision requested",
};
export const HOLD_LABEL: Record<string, string> = {
  None: "None",
  Hold: "On hold",
  Cleared: "Cleared",
  ClearedWithConditions: "Cleared with conditions",
};
export const REVIEW_LABEL: Record<string, string> = {
  NotReviewed: "To review",
  Reviewed: "Reviewed",
  ReReviewRequested: "Re-review requested",
  FamilyUpdated: "Family updated",
};
export const REASON_LABEL: Record<string, string> = {
  Meds: "Medication",
  SelfCarry: "Self-carry",
  Allergy: "Allergy",
  Adult18: "18+",
  Flagged: "Flagged",
  CheckinAnswer: "Check-in answer",
};

type View = "toReview" | "reviewed" | "familyUpdated" | "reReview" | "holds" | "changedSinceFreeze" | "all";
const VIEWS: { value: View; label: string }[] = [
  { value: "toReview", label: "To review" },
  { value: "familyUpdated", label: "Family updated after review" },
  { value: "reReview", label: "Re-review requested" },
  { value: "reviewed", label: "Reviewed" },
  { value: "holds", label: "Attendance holds" },
  { value: "changedSinceFreeze", label: "Changed since the seed freeze" },
  { value: "all", label: "Everyone" },
];

function inView(p: QueuePerson, view: View, showReviewed: boolean): boolean {
  switch (view) {
    case "toReview":
      return p.reviewStatus !== "Reviewed" || showReviewed;
    case "reviewed":
      return p.reviewStatus === "Reviewed";
    case "familyUpdated":
      return p.familyUpdatedAfterReview || p.reviewStatus === "FamilyUpdated";
    case "reReview":
      return p.reviewStatus === "ReReviewRequested";
    case "changedSinceFreeze":
      return p.changedSinceFreeze === true;
    case "holds":
      return p.holdRequested || (p.attendanceHold !== "None" && p.attendanceHold !== "");
    default:
      return showReviewed || p.reviewStatus !== "Reviewed";
  }
}

function StatusBadge({ s }: { s: string }) {
  const cls =
    s === "Reviewed"
      ? "bg-emerald-100 text-emerald-900"
      : s === "FamilyUpdated"
        ? "bg-amber-100 text-amber-900"
        : s === "ReReviewRequested"
          ? "bg-violet-100 text-violet-900"
          : "bg-slate-100 text-slate-800";
  return <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${cls}`}>{REVIEW_LABEL[s] ?? (s || "—")}</span>;
}

/** The Director's decision, when there is one (the request itself shows as a flag). */
export function HoldBadge({ hold }: { hold: string }) {
  if (!hold || hold === "None") return null;
  const cls = hold === "Hold" ? "bg-red-700 text-white" : hold === "ClearedWithConditions" ? "bg-amber-100 text-amber-900" : "bg-emerald-100 text-emerald-900";
  return <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${cls}`}>Attendance: {HOLD_LABEL[hold] ?? hold}</span>;
}

function Flags({ p }: { p: QueuePerson }) {
  const hasHold = Boolean(p.attendanceHold) && p.attendanceHold !== "None";
  if (!p.flagTypes.length && !hasHold && !p.changedSinceFreeze) return <span className="text-slate-400">—</span>;
  return (
    <span className="flex flex-wrap gap-1">
      <HoldBadge hold={p.attendanceHold} />
      {p.changedSinceFreeze && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-900">Changed since freeze</span>}
      {p.flagTypes.map((t) => (
        <span key={t} className="rounded bg-red-50 px-1.5 py-0.5 text-xs text-red-900">{FLAG_LABEL[t] ?? t}</span>
      ))}
    </span>
  );
}

export function ReviewQueue(): React.ReactElement {
  const [people, setPeople] = useState<QueuePerson[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<View>("toReview");
  const [flag, setFlag] = useState("");
  const [showReviewed, setShowReviewed] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    void api<{ people: QueuePerson[] }>("GET", "/api/roster").then((r) => {
      if (r.ok) setPeople(r.data.people);
      else setError(r.data.error ?? "Could not load the queue.");
    });
  }, []);

  if (error) return <div className="section-card" role="alert"><p className="text-sm text-red-700">{error}</p></div>;
  if (!people) return <p className="text-sm text-slate-600">Loading…</p>;

  const query = q.trim().toLowerCase();
  const shown = people.filter(
    (p) =>
      inView(p, view, showReviewed) &&
      (!flag || (flag === "ExpectedAtMedStation" ? p.expectedReasons.length > 0 : p.flagTypes.includes(flag))) &&
      (!query || `${p.firstName} ${p.lastName} ${p.lastName}, ${p.firstName} ${p.personKey}`.toLowerCase().includes(query))
  );
  const counts = {
    toReview: people.filter((p) => p.reviewStatus !== "Reviewed").length,
    reviewed: people.filter((p) => p.reviewStatus === "Reviewed").length,
  };

  return (
    <section>
      <h2 className="mb-1 text-xl font-semibold">Record review</h2>
      <p className="mb-4 text-sm text-slate-600">
        {counts.toReview} to review · {counts.reviewed} reviewed · {people.length} on the roster
      </p>
      <div className="section-card">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label htmlFor="q-view" className="field-label">Show</label>
            <select id="q-view" className="field-input" value={view} onChange={(e) => setView(e.target.value as View)}>
              {VIEWS.map((v) => (
                <option key={v.value} value={v.value}>{v.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="q-flag" className="field-label">Flag</label>
            <select id="q-flag" className="field-input" value={flag} onChange={(e) => setFlag(e.target.value)}>
              <option value="">Any (or none)</option>
              {Object.entries(FLAG_LABEL).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>
          <div className="min-w-0 flex-1">
            <label htmlFor="q-search" className="field-label">Find</label>
            <input id="q-search" className="field-input w-full" placeholder="Name or reference" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <label className="flex items-center gap-2 pb-2 text-sm">
            <input type="checkbox" checked={showReviewed} onChange={(e) => setShowReviewed(e.target.checked)} />
            Show reviewed
          </label>
        </div>
      </div>

      <div className="section-card">
        <p className="mb-2 text-sm text-slate-600" role="status">{shown.length} shown</p>
        {people.length === 0 && <p className="text-sm text-slate-600">No one on the roster yet. A System Admin reads the applications from the Roster screen.</p>}
        {shown.length > 0 && (
          <>
            <ul className="divide-y divide-slate-200 lg:hidden">
              {shown.map((p) => (
                <li key={p.personKey} className="py-3 text-sm">
                  <a className="font-medium text-program underline" href={`/review/${encodeURIComponent(p.personKey)}`}>
                    {p.lastName}, {p.firstName}
                  </a>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-slate-600">
                    <span>{p.personKey}</span>
                    {p.city && <span>· {p.city}</span>}
                    <StatusBadge s={p.reviewStatus} />
                  </div>
                  {p.familyUpdatedAfterReview && <p className="mt-1 font-medium text-amber-900">Family updated after review</p>}
                  <div className="mt-1"><Flags p={p} /></div>
                  {p.expectedReasons.length > 0 && (
                    <p className="mt-1 text-slate-700">Med station: {p.expectedReasons.map((r) => REASON_LABEL[r] ?? r).join(", ")}</p>
                  )}
                </li>
              ))}
            </ul>
            <table className="hidden w-full table-fixed text-left text-sm lg:table">
              <colgroup>
                <col className="w-[19%]" />
                <col className="w-[14%]" />
                <col className="w-[9%]" />
                <col className="w-[13%]" />
                <col className="w-[19%]" />
                <col className="w-[15%]" />
                <col className="w-[11%]" />
              </colgroup>
              <thead className="border-b border-slate-200 text-slate-500">
                <tr>
                  <th className="py-2 pr-2 font-medium">Name</th>
                  <th className="py-2 pr-2 font-medium">Reference</th>
                  <th className="py-2 pr-2 font-medium">City</th>
                  <th className="py-2 pr-2 font-medium">Review</th>
                  <th className="py-2 pr-2 font-medium">Flags</th>
                  <th className="py-2 pr-2 font-medium">Med station</th>
                  <th className="py-2 font-medium">Updated after review</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {shown.map((p) => (
                  <tr key={p.personKey} className="align-top">
                    <td className="break-words py-2 pr-2">
                      <a className="font-medium text-program underline" href={`/review/${encodeURIComponent(p.personKey)}`}>
                        {p.lastName}, {p.firstName}
                      </a>
                    </td>
                    <td className="break-all py-2 pr-2">{p.personKey}</td>
                    <td className="break-words py-2 pr-2">{p.city || <span className="text-slate-400">—</span>}</td>
                    <td className="py-2 pr-2"><StatusBadge s={p.reviewStatus} /></td>
                    <td className="py-2 pr-2"><Flags p={p} /></td>
                    <td className="break-words py-2 pr-2">{p.expectedReasons.map((r) => REASON_LABEL[r] ?? r).join(", ") || <span className="text-slate-400">—</span>}</td>
                    <td className="py-2">{p.familyUpdatedAfterReview ? <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-900">Yes</span> : <span className="text-slate-400">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </section>
  );
}
